import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchQrPoint, requestQrCode, verifyQrCode, startQrInspection, QrPointPublic } from '../services/qrApi';
import { publicBodyStyle, publicCardStyle } from './shared/PublicPage';

/**
 * Sidan bakom QR-lappen på en stuga/plats: den som utför kontroller skannar och
 * startar den kopplade kontrollen. Första gången identifierar man sig med
 * sms-engångskod; sedan minns telefonen vem det är (enhetstoken).
 */

const DEVICE_KEY = 'qr_device_token';

const bodyStyle: React.CSSProperties = { ...publicBodyStyle, padding: '16px' };
const cardStyle: React.CSSProperties = { ...publicCardStyle(480), padding: '24px' };
const inputStyle: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: 6,
    padding: '10px 12px', fontSize: 16, fontFamily: 'inherit', marginBottom: 10,
};
const primaryBtn: React.CSSProperties = {
    background: '#2563eb', color: 'white', border: 'none', borderRadius: 6,
    padding: '13px 24px', fontSize: 16, cursor: 'pointer', fontFamily: 'inherit', width: '100%',
};
const linkBtn: React.CSSProperties = {
    background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer',
    fontSize: 14, fontFamily: 'inherit', textDecoration: 'underline', padding: 0,
};
const errorBox: React.CSSProperties = { background: '#fde8e8', color: '#b42318', borderRadius: 8, padding: 12, marginBottom: 14, fontSize: 14 };
const okBox: React.CSSProperties = { background: '#e7f6ec', color: '#15803d', borderRadius: 8, padding: 12, marginBottom: 14, fontSize: 14 };

const readDeviceToken = (): string | null => { try { return localStorage.getItem(DEVICE_KEY); } catch { return null; } };
const writeDeviceToken = (t: string | null) => {
    try { t ? localStorage.setItem(DEVICE_KEY, t) : localStorage.removeItem(DEVICE_KEY); } catch { /* ignore */ }
};

const QrPage: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();

    const [point, setPoint] = useState<QrPointPublic | null>(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    // Identifiering: telefonnummer → sms-kod → telefonen minns.
    // Den som inte är igenkänd hamnar direkt på mobilnummersteget.
    const [identifying, setIdentifying] = useState(false);
    const [phone, setPhone] = useState('');
    const [code, setCode] = useState('');
    const [codeSent, setCodeSent] = useState(false);

    useEffect(() => {
        if (!token) return;
        fetchQrPoint(token, readDeviceToken())
            .then(p => { setPoint(p); if (!p.viewer && p.can_inspect) setIdentifying(true); })
            .catch(err => { if (err?.response?.status === 404) setNotFound(true); else setError('Kunde inte hämta sidan. Försök igen.'); })
            .finally(() => setLoading(false));
    }, [token]);

    const apiError = (err: any, fallback: string) =>
        setError(err?.response?.data?.error || fallback);

    const startInspection = async () => {
        const deviceToken = readDeviceToken();
        if (!deviceToken) { setIdentifying(true); return; }
        setBusy(true); setError(null);
        try {
            const out = await startQrInspection(token!, deviceToken);
            navigate(`/inspection/${out.inspection_token}?c=${out.viewer.id}`);
        } catch (err: any) {
            if (err?.response?.status === 401) {
                // Enhetstoken har tynat bort — identifiera igen.
                writeDeviceToken(null);
                setPoint(p => (p ? { ...p, viewer: null } : p));
                setIdentifying(true);
            } else {
                apiError(err, 'Kunde inte starta kontrollen. Försök igen.');
            }
        } finally { setBusy(false); }
    };

    const sendCode = async () => {
        if (!phone.trim()) { setError('Skriv ditt mobilnummer'); return; }
        setBusy(true); setError(null);
        try {
            await requestQrCode(token!, phone.trim());
            setCodeSent(true);
        } catch (err: any) {
            apiError(err, 'Kunde inte skicka koden. Försök igen.');
        } finally { setBusy(false); }
    };

    const verifyCode = async () => {
        if (!code.trim()) { setError('Skriv koden från sms:et'); return; }
        setBusy(true); setError(null);
        try {
            const out = await verifyQrCode(token!, phone.trim(), code.trim());
            writeDeviceToken(out.device_token);
            setPoint(p => (p ? { ...p, viewer: out.viewer } : p));
            setIdentifying(false); setCode(''); setCodeSent(false);
        } catch (err: any) {
            apiError(err, 'Fel kod. Försök igen.');
        } finally { setBusy(false); }
    };

    if (loading) return <div style={bodyStyle}><div style={cardStyle}>Laddar…</div></div>;
    if (notFound || !point) return (
        <div style={bodyStyle}><div style={cardStyle}>
            <h2 style={{ marginTop: 0 }}>Länken hittades inte</h2>
            <p>QR-koden är inte längre giltig. Kontakta den som satt upp lappen.</p>
        </div></div>
    );

    return (
        <div style={bodyStyle}>
            <div style={cardStyle}>
                <h2 style={{ margin: '0 0 4px' }}>{point.name}</h2>
                {point.zone_name && point.zone_name !== point.name && (
                    <div style={{ color: '#666', fontSize: 14, marginBottom: 8 }}>{point.zone_name}</div>
                )}
                {error && <div style={errorBox}>{error}</div>}

                {!identifying && (
                    <>
                        {point.viewer && (
                            <div style={okBox}>
                                Hej {point.viewer.name}!{' '}
                                <button style={{ ...linkBtn, fontSize: 13 }}
                                        onClick={() => { writeDeviceToken(null); setPoint({ ...point, viewer: null }); setIdentifying(true); }}>
                                    Inte du?
                                </button>
                            </div>
                        )}
                        <div style={{ marginTop: 8 }}>
                            {point.can_inspect ? (
                                <button style={primaryBtn} disabled={busy} onClick={startInspection}>
                                    {busy ? 'Startar…' : `Gör kontrollen${point.template_name ? `: ${point.template_name}` : ''}`}
                                </button>
                            ) : (
                                <p style={{ color: '#666' }}>Lappen är inte kopplad till någon kontroll ännu.</p>
                            )}
                        </div>
                    </>
                )}

                {identifying && (
                    <>
                        <p style={{ fontSize: 14, color: '#444' }}>
                            Skriv ditt mobilnummer, så skickar vi en kod via sms.
                            Den här telefonen kommer sedan ihåg vem du är.
                        </p>
                        {!codeSent ? (
                            <>
                                <input style={inputStyle} type="tel" placeholder="Mobilnummer, t.ex. 070-123 45 67"
                                       value={phone} onChange={e => setPhone(e.target.value)} autoFocus />
                                <button style={primaryBtn} disabled={busy} onClick={sendCode}>
                                    {busy ? 'Skickar…' : 'Skicka kod'}
                                </button>
                            </>
                        ) : (
                            <>
                                <div style={okBox}>Om numret finns upplagt har en kod skickats via sms.</div>
                                <input style={inputStyle} inputMode="numeric" placeholder="Koden från sms:et"
                                       value={code} onChange={e => setCode(e.target.value)} autoFocus />
                                <button style={primaryBtn} disabled={busy} onClick={verifyCode}>
                                    {busy ? 'Kontrollerar…' : 'Fortsätt'}
                                </button>
                                <div style={{ marginTop: 10, textAlign: 'center' }}>
                                    <button style={linkBtn} disabled={busy} onClick={() => { setCodeSent(false); setCode(''); }}>
                                        Fick du ingen kod? Försök igen
                                    </button>
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default QrPage;
